<template>
  <div>
    <div v-if="!showScPdf">
      <loading-status-box
        v-if="callScorecardDataV2LoadingStatus != 'loaded'"
        :message="scorecardLoadingMessage"
        :type="scorecardLoadingType"
        :messageStyle="loadingStyle"
      >
      </loading-status-box>

      <call-scorecard-body-table
        v-else
        :callData="callData"
        :callId="callId"
        :format="'callScorecard'"
        :displayedRegions="displayedRegions"
        :clearAppliedChanges="clearAppliedChanges"
        :excludeCall="excludeCall"
        :typeOfExclusion="typeOfExclusion"
        :changesApplied="changesApplied"
        :changesRemoved="changesRemoved"
        :pageQueryData="pageQueryData"
        :selfSigned="selfSigned"
        :showTask="showTask"
        @spliceDisplayedRegions="spliceDisplayedRegions"
        @setDisplayedRegions="setDisplayedRegions"
        @clearAppliedChanges="clearScorecardChanges"
        @changeCallOverrides="applyScorecardChanges"
        @deleteAppliedOverrides="removeExistingScChanges"
      ></call-scorecard-body-table>
    </div>
    <div v-else>
      <loading-status-box
        v-if="pdfSnapshotLoading || noPdfSnapshot"
        :message="scorecardPdfLoadingMessage"
        :type="scorecardPdfLoadingType"
        :messageStyle="loadingStyle"
      >
      </loading-status-box>
      <iframe
        v-else-if="taskSnapshotExists"
        width="100%"
        :style="snapshotHeightStyle"
        :src="taskSnapshot.snapshot_url + pdfIframeParams"
      ></iframe>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import LoadingStatusBox from "@/components/Widgets/LoadingStatusBox.vue";
import CallScorecardBodyTable from "./CallScorecardBodyTable.vue";

const MIN_CARD_HEIGHT = 25;
const INNER_CARD_HEIGHT_RELATION = 40;

export default {
  components: {
    LoadingStatusBox,
    CallScorecardBodyTable
  },
  data() {
    return { snapshotHeight: 0, snapshotHeightStyle: "" };
  },
  props: {
    callId: {
      required: true,
      type: Number
    },
    callData: {
      required: true,
      type: Object
    },
    displayedRegions: {
      required: false,
      type: Array,
      default() {
        return [];
      }
    },
    clearAppliedChanges: {
      required: true,
      type: Boolean
    },
    excludeCall: {
      required: true,
      type: Boolean
    },
    typeOfExclusion: {
      required: false,
      type: Number,
      default: null
    },
    changesApplied: {
      required: true,
      type: Boolean
    },
    changesRemoved: {
      required: true,
      type: Boolean
    },
    pageQueryData: {
      type: Object,
      required: false,
      default: null
    },
    selfSigned: {
      type: Boolean,
      required: false,
      default: false
    },
    showTask: {
      type: Boolean,
      required: false,
      default: false
    },
    showScPdf: {
      type: Boolean,
      required: false,
      default: false
    },
    innerCardHeight: {
      type: Number,
      default: 0
    }
  },
  computed: {
    ...mapGetters("scorecardsV2Calls", [
      "callScorecardDataV2",
      "callScorecardDataV2LoadingStatus",
      "callScorecardDataV2LoadingError",
      "callScorecardDataV2LoadingErrorStatus"
    ]),
    ...mapGetters("scorecardTasks", [
      "taskSnapshot",
      "taskSnapshotLoadingStatus",
      "taskSnapshotLoadingError",
      "scorecardTaskList",
      "scorecardTaskListLoadingStatus"
    ]),
    ...mapGetters("tasks", [
      "taskData",
      "taskDataLoadingStatus",
      "taskDataLoadingError",
      "taskDataLoadingErrorStatus"
    ]),
    pdfIframeParams() {
      return "#toolbar=0";
    },
    taskSnapshotExists() {
      return (
        this.taskSnapshot &&
        this.taskSnapshot.snapshot_url &&
        this.taskSnapshot.snapshot_url.length > 0 &&
        this.taskSnapshot.call_id == this.callId
      );
    },
    loadingStyle() {
      return "text-wrap: nowrap; font-size:14px";
    },
    scorecardLoadingType() {
      if (this.callScorecardDataV2LoadingError) return "error";
      else return "loading";
    },
    scorecardLoadingMessage() {
      if (this.callScorecardDataV2LoadingError)
        return "There was an error loading this scorecard...";
      else return "Scorecard loading, please wait...";
    },
    scorecardPdfLoadingType() {
      if (this.taskSnapshotLoadingError) return "error";
      else if (this.noPdfSnapshot) return "nodata";
      else return "loading";
    },
    scorecardPdfLoadingMessage() {
      if (this.taskSnapshotLoadingError)
        return "There was an error loading this scorecard snapshot...";
      else if (this.noPdfSnapshot)
        return "There was no scorecard snapshot created for this scorecard";
      else return "Scorecard snapshot loading, please wait...";
    },
    noPdfSnapshot() {
      return ((this.selfSigned && this.taskDataLoadingStatus == "loaded") ||
        (!this.selfSigned &&
          this.scorecardTaskListLoadingStatus == "loaded")) &&
        this.taskSnapshotLoadingStatus == "loaded"
        ? !this.taskSnapshotExists
        : false;
    },
    pdfSnapshotLoading() {
      return (
        (this.selfSigned && this.taskDataLoadingStatus != "loaded") ||
        (!this.selfSigned && this.scorecardTaskListLoadingStatus != "loaded") ||
        this.taskSnapshotLoadingStatus != "loaded"
      );
    }
  },
  methods: {
    spliceDisplayedRegions(index) {
      this.$emit("spliceDisplayedRegions", index);
    },
    setDisplayedRegions(regions) {
      this.$emit("setDisplayedRegions", regions);
    },
    clearScorecardChanges(clear) {
      this.$emit("clearAppliedChanges", clear);
    },
    applyScorecardChanges(apply) {
      this.$emit("changeCallOverrides", apply);
    },
    removeExistingScChanges(remove) {
      this.$emit("deleteAppliedOverrides", remove);
    },
    setHeights() {
      //if the body does not have a header, account for more scroll height
      this.snapshotHeight = Math.max(
        this.innerCardHeight - INNER_CARD_HEIGHT_RELATION,
        MIN_CARD_HEIGHT
      );
      this.snapshotHeightStyle =
        "height: " + this.snapshotHeight + "px !important;";
    },
    debouncedResize() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.setHeights();
      }, 500);
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.debouncedResize);
  },
  mounted() {
    this.setHeights();
    window.addEventListener("resize", this.debouncedResize);
  },
  watch: { innerCardHeight: "setHeights" }
};
</script>

<style scoped></style>
